/**
 * PermissionsDrawer — the per-member permission editor. Two layers, exactly mirroring the server model:
 *   1. Preset bundles + ownership   → setMembership (assignMembership in iam; one-actif advisory lock)
 *   2. Fine-grained grant/deny deltas → addGrant / removeGrant (catalog-validated; deny-wins, F-03)
 *
 * The deny-wins posture is shown explicitly: a `deny` delta is badged danger ("refus prioritaire") and
 * always overrides any preset/grant for that capability in the live effective set. The server is the
 * trust boundary — every write is re-checked + the live capability cache invalidated; the api layer also
 * re-pulls the caller's own effective set so the cockpit re-gates immediately.
 *
 * Gated entirely under iam.utilisateur.manage (the drawer only opens from the staff table, itself gated).
 */
import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  FormField,
  Input,
  Select,
  Table,
  Textarea,
  useToast,
  type Column,
} from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { useAddGrant, useRemoveGrant, useSetMembership } from "../api";
import type { GrantEffet, StaffGrant, StaffMember } from "../types";
import { PresetPicker } from "./PresetPicker";

export interface PermissionsDrawerProps {
  member: StaffMember;
  labId: string | null;
}

export function PermissionsDrawer({ member, labId }: PermissionsDrawerProps) {
  const { t, locale } = useT();
  const toast = useToast();
  const setMembership = useSetMembership(labId);
  const addGrant = useAddGrant(labId);
  const removeGrant = useRemoveGrant(labId);

  const [presets, setPresets] = useState<string[]>(member.presets);
  const [owner, setOwner] = useState<boolean>(member.estProprietaire);

  // New-grant form
  const [capability, setCapability] = useState("");
  const [effet, setEffet] = useState<GrantEffet>("grant");
  const [motif, setMotif] = useState("");
  const [grantError, setGrantError] = useState<string | null>(null);

  // Reset local form whenever a different member is opened.
  useEffect(() => {
    setPresets(member.presets);
    setOwner(member.estProprietaire);
    setCapability("");
    setEffet("grant");
    setMotif("");
    setGrantError(null);
  }, [member.staffId, member.presets, member.estProprietaire]);

  const savePresets = () => {
    setMembership.mutate(
      { utilisateur_id: member.utilisateur.id, presets, est_proprietaire: owner },
      {
        onSuccess: () => toast.success(t("admin.team.perms.presetSaved")),
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("admin.toast.error")),
      },
    );
  };

  const submitGrant = () => {
    setGrantError(null);
    if (!capability.trim()) {
      setGrantError(t("admin.field.required"));
      return;
    }
    addGrant.mutate(
      {
        utilisateur_id: member.utilisateur.id,
        laboratoire_staff_id: member.staffId,
        capability_cle: capability.trim(),
        effet,
        ...(motif.trim() ? { motif: motif.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("admin.team.perms.grant.added"));
          setCapability("");
          setMotif("");
          setEffet("grant");
        },
        onError: (err) => setGrantError(err instanceof ApiError ? err.message : t("admin.toast.error")),
      },
    );
  };

  const dropGrant = (grant: StaffGrant) => {
    removeGrant.mutate(
      { grantId: grant.id, utilisateur_id: member.utilisateur.id },
      {
        onSuccess: () => toast.success(t("admin.team.perms.grant.removed")),
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("admin.toast.error")),
      },
    );
  };

  const grantColumns: ReadonlyArray<Column<StaffGrant>> = [
    {
      key: "capability",
      header: t("admin.team.perms.grant.col.capability"),
      mono: true,
      cell: (g) => <span className="nv-ltr-island">{g.capabilityCle}</span>,
    },
    {
      key: "effet",
      header: t("admin.team.perms.grant.col.effet"),
      cell: (g) =>
        g.effet === "deny" ? (
          <Badge tone="danger" glyph="⊘">
            {t("admin.team.perms.denyWins")}
          </Badge>
        ) : (
          <Badge tone="success" glyph="✓">
            {t("admin.team.perms.grantTone")}
          </Badge>
        ),
    },
    { key: "motif", header: t("admin.team.perms.grant.col.motif"), cell: (g) => g.motif || "—" },
    {
      key: "created",
      header: t("admin.team.perms.grant.col.created"),
      cell: (g) => (
        <span className="nv-ltr-island">
          {g.createdAt
            ? new Intl.DateTimeFormat(locale === "ar" ? "ar-DZ" : "fr-DZ", { dateStyle: "short" }).format(
                new Date(g.createdAt),
              )
            : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "end",
      cell: (g) => (
        <Button size="sm" variant="ghost" onClick={() => dropGrant(g)} disabled={removeGrant.isPending}>
          {t("admin.team.perms.grant.remove")}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      {/* Layer 1 — presets + ownership */}
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "var(--nv-fs-title)" }}>{t("admin.team.perms.presets")}</h3>
          <p style={{ margin: "var(--nv-s-1) 0 0", color: "var(--nv-text-3, var(--nv-steel))", fontSize: "var(--nv-fs-caption)" }}>
            {t("admin.team.perms.presets.hint")}
          </p>
        </div>
        <PresetPicker value={presets} onChange={setPresets} disabled={setMembership.isPending} />
        <label style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
          <input
            type="checkbox"
            checked={owner}
            disabled={setMembership.isPending}
            onChange={(e) => setOwner(e.target.checked)}
          />
          <span>
            {t("admin.team.perms.owner")}
            <span style={{ display: "block", color: "var(--nv-text-3, var(--nv-steel))", fontSize: "var(--nv-fs-caption)" }}>
              {t("admin.team.perms.owner.hint")}
            </span>
          </span>
        </label>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={savePresets} loading={setMembership.isPending}>
            {t("admin.team.perms.savePreset")}
          </Button>
        </div>
      </section>

      {/* Layer 2 — fine-grained grants/denies (deny-wins) */}
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "var(--nv-fs-title)" }}>{t("admin.team.perms.grants")}</h3>
          <p style={{ margin: "var(--nv-s-1) 0 0", color: "var(--nv-text-3, var(--nv-steel))", fontSize: "var(--nv-fs-caption)" }}>
            {t("admin.team.perms.grants.hint")}
          </p>
        </div>

        {member.grants.length === 0 ? (
          <p style={{ margin: 0, color: "var(--nv-text-3, var(--nv-steel))" }}>
            {t("admin.team.perms.grant.none")}
          </p>
        ) : (
          <Table
            columns={grantColumns}
            rows={member.grants}
            rowKey={(g) => g.id}
            caption={t("admin.team.perms.grants")}
          />
        )}

        {/* Add-grant form */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--nv-s-3)",
            padding: "var(--nv-s-3)",
            border: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
            borderRadius: "var(--nv-r-sm)",
          }}
        >
          <strong>{t("admin.team.perms.grant.add")}</strong>
          <FormField label={t("admin.team.perms.grant.capability")} required>
            {(p) => (
              <Input
                {...p}
                value={capability}
                onChange={(e) => setCapability(e.target.value)}
                placeholder={t("admin.team.perms.grant.capability.placeholder")}
                className="nv-ltr-island"
              />
            )}
          </FormField>
          <FormField label={t("admin.team.perms.grant.effet")}>
            {(p) => (
              <Select
                {...p}
                value={effet}
                onChange={(e) => setEffet(e.target.value as GrantEffet)}
                options={[
                  { value: "grant", label: t("admin.team.perms.grant.effet.grant") },
                  { value: "deny", label: t("admin.team.perms.grant.effet.deny") },
                ]}
              />
            )}
          </FormField>
          <FormField label={t("admin.team.perms.grant.motif")}>
            {(p) => <Textarea {...p} value={motif} onChange={(e) => setMotif(e.target.value)} rows={2} />}
          </FormField>
          {grantError && (
            <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
              {grantError}
            </p>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={submitGrant} loading={addGrant.isPending}>
              {t("admin.team.perms.grant.submit")}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
